import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { SocketEventAction } from '../../../types/api/SocketEventAction';
import { IFileItem, isFileItem } from '../../../types/api/FileItem';
import { useBrowserPushEventId } from '../../ViewFetcher';
import { isNullOrUndefined } from '../../../lib/typeGuards';
import { useWebSockets } from '../../WebSocketManager';

const createEventName = (any: (string | undefined)[]) => any.filter(string => !isNullOrUndefined(string)).join(':');

type UseWebsocketSyncedAttachments<T> = {
  syncedAttachments: T[];
  setSyncedAttachments: Dispatch<SetStateAction<T[]>>;
};

/**
 * Sync Attachments with Websockets.
 *
 * This will listen to the browserPushEventId of the block
 *    -> browserPushEventId:SocketEventAction
 * A postId can be provided to listen to a specific connection of a post
 *    -> browserPushEventId:postId:SocketEventAction
 */
export function useWebsocketSyncedAttachments<T extends IFileItem>(
  initialState?: T[],
  postId?: string
): UseWebsocketSyncedAttachments<T> {
  const [syncedAttachments, setSyncedAttachments] = useState<T[]>(initialState || []);
  const browserPushEventId = useBrowserPushEventId();
  const subscribe = useWebSockets();

  const createEvent = createEventName([browserPushEventId, postId, SocketEventAction.CREATE]);
  const updateEvent = createEventName([browserPushEventId, postId, SocketEventAction.UPDATE]);
  const deleteEvent = createEventName([browserPushEventId, postId, SocketEventAction.DELETE]);

  const onCreate = useCallback((item: T) => {
    if (isFileItem(item)) {
      setSyncedAttachments(prev => [item, ...prev]);
    }
  }, []);

  const onUpdate = useCallback((item: T) => {
    if (isFileItem(item)) {
      setSyncedAttachments(prev => prev.map(attachment => (item.id === attachment.id ? item : attachment)));
    }
  }, []);

  const onDelete = useCallback((item: T) => {
    if (isFileItem(item)) {
      setTimeout(() => {
        setSyncedAttachments(prev => prev.filter(attachment => attachment.id !== item.id));
      }, 2000);
    }
  }, []);

  useEffect(() => subscribe(createEvent, onCreate), [subscribe, createEvent, onCreate]);
  useEffect(() => subscribe(updateEvent, onUpdate), [subscribe, updateEvent, onUpdate]);
  useEffect(() => subscribe(deleteEvent, onDelete), [subscribe, deleteEvent, onDelete]);

  return { syncedAttachments, setSyncedAttachments };
}
